import {
  FileUploadStatus,
  PointOfInterestDocument,
  usePoiImageCaptionMutation,
  useUpdatePointOfInterestFileMutation,
} from '@/__generated__/graphql';
import { Attachment } from '@/components/shared/AttachmentDisplay/type';
import { useWindowSize } from '../../../../../../../../hooks/useWindowSize';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { uploadFileToUrl } from '../UploadFileToUrl';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { AttachmentDisplay } from '@/components/shared/AttachmentDisplay';

type Props = {
  selectedAttachment: Attachment;
  selectedFileIndex: number;
  handleRemoveAttachment?: (index: number) => void;
  showRemoveIcon: boolean;
  enableUpload?: boolean;
  onUploadComplete?: (index: number, file: Attachment) => void;
  pointOfInterestId?: string;
  setAttachments?: React.Dispatch<React.SetStateAction<Attachment[]>>;
};

export const RenderAttachment = ({
  selectedAttachment,
  selectedFileIndex,
  handleRemoveAttachment,
  showRemoveIcon,
  enableUpload = false,
  onUploadComplete,
  pointOfInterestId,
  setAttachments = () => {},
}: Props) => {
  const screenSize = useWindowSize();
  const [updatePoiFile] = useUpdatePointOfInterestFileMutation();
  const [addPoiImageCaption] = usePoiImageCaptionMutation();
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState(false);
  const [imageCaption, setImageCaption] = useState<string>(selectedAttachment.name);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (hasError) {
        e.stopPropagation();
        e.preventDefault();
      }
    },
    [hasError]
  );

  const handleError = useCallback(
    (success: boolean) => {
      setHasError(!success);

      if (!success) {
        setAttachments((previous) => {
          return previous.filter(
            (attachment) => !attachment?.url?.includes(selectedAttachment.name)
          );
        });
      }
    },
    [selectedAttachment.name, setAttachments]
  );

  useEffect(() => {
    const uploadAttachment = async () => {
      if (
        enableUpload &&
        selectedAttachment.isNew &&
        selectedAttachment.url &&
        selectedAttachment.file &&
        selectedAttachment.id &&
        pointOfInterestId
      ) {
        const uploadFile = await uploadFileToUrl(
          selectedAttachment,
          selectedAttachment.url,
          setUploadProgress
        );
        if (uploadFile && selectedAttachment.id) {
          await updatePoiFile({
            variables: {
              input: {
                pointOfInterestId,
                fileId: selectedAttachment.id,
                uploadStatus: FileUploadStatus.Success,
              },
            },

            onError: (error) => {
              showSnackBar(error.message, 'error');
            },
          });
          if (onUploadComplete) onUploadComplete(selectedFileIndex, selectedAttachment);
        }
      }
    };

    uploadAttachment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enableUpload,
    selectedAttachment,
    updatePoiFile,
    onUploadComplete,
    selectedFileIndex,
    pointOfInterestId,
  ]);

  const handleImageCaption = async () => {
    setIsEditing(false);
    if (
      pointOfInterestId &&
      selectedAttachment.id &&
      imageCaption.trim() &&
      imageCaption !== selectedAttachment.caption
    ) {
      try {
        await addPoiImageCaption({
          variables: {
            input: {
              pointOfInterestId,
              poiAttachmentId: selectedAttachment.id,
              caption: imageCaption,
              lastModifiedFor: 'Update attachment caption',
            },
          },
          refetchQueries: [PointOfInterestDocument],
        });
        showSnackBar('Image caption added', 'success');
      } catch (error) {
        showSnackBar('Failed to add image caption', 'error');
      }
    }
  };

  useEffect(() => {
    setImageCaption(selectedAttachment.caption ?? selectedAttachment.name);
  }, [selectedAttachment]);

  const handleCaption = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const cardHeight = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1440) {
      return '70px';
    }
    return '100px';
  }, [screenSize]);

  return (
    <AttachmentDisplay
      selectedAttachment={selectedAttachment}
      selectedAttachmentIndex={selectedFileIndex}
      showRemoveIcon={showRemoveIcon}
      handleRemoveAttachment={handleRemoveAttachment}
      isEditing={isEditing}
      setIsEditing={setIsEditing}
      imageCaption={imageCaption}
      setImageCaption={setImageCaption}
      handleImageCaption={handleImageCaption}
      handleCaption={handleCaption}
      uploadProgress={uploadProgress}
      enableUpload={enableUpload}
      cardHeight={cardHeight}
      handleClick={handleClick}
      hasError={hasError}
      handleError={handleError}
    />
  );
};
